import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ISessionAttendanceFE, SessionAttendanceState } from '../../models/session-attendance/session-attendance.model';
import { ISelectButtonColoredItem, SelectButtonColoredComponent } from '../select-button-colored/select-button-colored.component';
import { Color } from '../../enums/utils/colors';
import { ISessionAttendanceChangeStateData, SessionAttendancesService } from '../../services/entities/session-attendances/session-attendances.service';
import { BehaviorSubject, Subject, Subscription, finalize, switchMap, take } from 'rxjs';
import { ReservationState } from '../../enums/reservation/reservation-states.enum';
import { SessionFormat } from '../../enums/session-group/session-formats.enum';
import { UserNameOrEmailPipe } from '../../pipes/user-name-or-email.pipe';
import { LocalizedDatePipe } from '../../pipes/localized-date.pipe';
import { LocationNamePipe } from '../../pipes/location-name.pipe';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ModalService } from '../../services/modal.service';
import { IUserSessionAttendanceFG, UserSessionAttendanceComponent } from './user-session-attendance/user-session-attendance.component';
import { StoreService } from '../../services/store/store.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-session-attendance',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    ButtonModule, ProgressSpinnerModule, TranslateModule,
    UserNameOrEmailPipe, LocalizedDatePipe, LocationNamePipe, UserSessionAttendanceComponent,
  ],
  templateUrl: './session-attendance.component.html',
  styleUrls: ['./session-attendance.component.scss']
})
export class SessionAttendanceComponent implements OnInit, OnDestroy {
  ReservationState = ReservationState;
  SessionAttendanceState = SessionAttendanceState;
  SessionFormat = SessionFormat;
  
  @Input() sessionUuid: string | undefined;

  form = new FormGroup({
    confirmed: new FormArray<FormGroup<IUserSessionAttendanceFG>>([]),
    approved: new FormArray<FormGroup<IUserSessionAttendanceFG>>([]),
  })

  fetching: boolean = false;
  saving: boolean = false;

  sessionAttendance$ = new BehaviorSubject<ISessionAttendanceFE | null>(null);

  watchFormSub: Subscription = Subscription.EMPTY;
  subs: Subscription[] = [];
  
  private saveSubject = new Subject<void>();

  constructor(
    private sessionAttendancesService: SessionAttendancesService,
    private modalService: ModalService,
    private storeService: StoreService
  ) {
    // Listen to the save subject and use switchMap to cancel previous subscriptions if a new one comes in
    this.saveSubject.pipe(
      switchMap(() => {
        if (this.form.invalid || this.saving) {
          console.error('invalid');
          return [];
        }
        if (!this.sessionAttendance$.value) return [];
        const formVals = [ ...this.form.controls.confirmed.getRawValue(), ...this.form.controls.approved.getRawValue() ];
        const d: ISessionAttendanceChangeStateData = {
          sessionId: this.sessionAttendance$.value.session.id,
          data: formVals.map(x => ({
            userId: x.userId,
            sessionAttendanceState: x.state,
            sessionAttendanceExcused: x.excused,
          }))
        };

        this.saving = true;
        return this.sessionAttendancesService.changeStates(d, { succSilent: true }).pipe(
          take(1),
          finalize(() => this.saving = false)
        );
      })
    ).subscribe(res => {
      // Handle successful save here
    });
  }

  ngOnInit(): void {
    this.fetchData();

    this.subs.push(
      this.storeService.actions.sessionAttendance_sessionAttendanceChanged$.subscribe(() => {
        this.fetchData();
      })
    )
  }

  onSave() {
    this.saveSubject.next(); // Trigger a new save operation
  }

  fetchData() {
    if (!this.sessionUuid) return;

    this.fetching = true;
    this.sessionAttendancesService.getBySession({ sessionUuid: this.sessionUuid }).pipe(
      take(1),
      finalize(() => this.fetching = false)
    ).subscribe({
      next: (res) => {
        this.sessionAttendance$.next( res );
        this.initForm();
      }
    });
  }

  

  

  private initForm() {
    this.watchFormSub.unsubscribe();
    if (!this.sessionAttendance$.value) {
      this.form.reset({
        approved: [],
        confirmed: [],
      }, { emitEvent: false });
      return;
    };

    const confirmedFGs = this.sessionAttendance$.value.attendances[ReservationState.CONFIRMED].map(x => {
      const sa = x.sessionAttendances.find(x => x.session.uuid === this.sessionUuid);
      const fg = new FormGroup<IUserSessionAttendanceFG>({
        state: new FormControl('state' in sa! ? sa.state : null, { nonNullable: true }),
        excused: new FormControl('excused' in sa! ? sa.excused : false, { nonNullable: true }),
        replaced: new FormControl('replaced' in sa! ? sa.replaced : false, { nonNullable: true }),
        sessionId: new FormControl(sa!.session.id, { nonNullable: true }),
        userId: new FormControl(x.user.id, { nonNullable: true }),
      });
      if (fg.controls.replaced.value) fg.disable();
      return fg;
    });
    const approvedFGs = this.sessionAttendance$.value.attendances[ReservationState.APPROVED].map(x => {
      const sa = x.sessionAttendances.find(x => x.session.uuid === this.sessionUuid);
      const fg = new FormGroup<IUserSessionAttendanceFG>({
        state: new FormControl('state' in sa! ? sa.state : null, { nonNullable: true }),
        excused: new FormControl('excused' in sa! ? sa.excused : false, { nonNullable: true }),
        replaced: new FormControl('replaced' in sa! ? sa.replaced : false, { nonNullable: true }),
        sessionId: new FormControl(sa!.session.id, { nonNullable: true }),
        userId: new FormControl(x.user.id, { nonNullable: true }),
      });
      if (fg.controls.replaced.value) fg.disable();
      return fg;
    });


    this.form.controls.confirmed.clear({ emitEvent: false });
    confirmedFGs.forEach(fg => {
      this.form.controls.confirmed.push(fg);
    })
    this.form.controls.approved.clear({ emitEvent: false });
    approvedFGs.forEach(fg => {
      this.form.controls.approved.push(fg);
    })

    
    this.watchFormSub = this.form.valueChanges.subscribe(v => {
          
      this.onSave();
    });
  }

  ngOnDestroy() {
    this.watchFormSub.unsubscribe();
    this.subs.forEach(sub => sub.unsubscribe());
  }
}
